import type { SafetyProgramEvidence, SafetyProgramOperation, SafetyProgramWorkspace } from '@kop/contracts';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';

export type ProgramProjection = { siteId: string; year: number; complete: boolean;
  summary: SafetyProgramWorkspace['summary']; reminders: SafetyProgramWorkspace['reminders'];
  inspectionSchedule: { equipmentId: string; name: string; dueDate: string }[];
  monthlyPlans: { month: string; title: string; planned: number; completed: number | null }[] };
type Transport = { load(input: Record<string, unknown>): Promise<unknown>; execute(input: Record<string, unknown>): Promise<unknown>;
  certificate?(action:'analyze'|'register',input:Record<string,unknown>):Promise<unknown>;
  evidence(action: 'prepare' | 'commit' | 'open', input: Record<string, unknown>): Promise<unknown> };
let epoch = 0;
onSensitiveMemoryCacheClear(() => { epoch += 1; });
export interface SafetyProgramPort {
  certificate?(action:'analyze'|'register',input:Record<string,unknown>):Promise<unknown>;
  load(year: number): Promise<SafetyProgramWorkspace>;
  summary(year: number): Promise<ProgramProjection>;
  save(operation: SafetyProgramOperation, id: string, version: number, payload: unknown, commandId: string): Promise<void>;
  upload(aggregateId: string, file: File, onProgress: (percent: number) => void, signal: AbortSignal): Promise<SafetyProgramEvidence>;
  open(evidence: SafetyProgramEvidence, disposition?: 'attachment'|'inline'): Promise<string>;
}
export function createSafetyProgramRepository(siteId: string, transport?: Promise<Transport>): SafetyProgramPort {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/u.test(siteId)) throw Error('SAFETY_PROGRAM_SITE_REQUIRED');
  const sessionEpoch = epoch;
  const current = () => { if (epoch !== sessionEpoch) throw Error('SAFETY_PROGRAM_SESSION_CHANGED'); };
  const port = transport ?? import('@kop/operations-firebase-adapter').then(module => module.createSafetyProgramOperationsPort({
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '', apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '', authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
  }));
  const scoped = async (year: number, view: 'workspace' | 'summary') => {
    current(); const client = await port; current();
    const value = await client.load({ siteId, year, view }); current();
    if (!value || typeof value !== 'object') throw Error('SAFETY_PROGRAM_RESPONSE_INVALID');
    const result = value as SafetyProgramWorkspace & ProgramProjection;
    if (result.siteId !== siteId || result.year !== year || typeof result.complete !== 'boolean' || !result.summary)
      throw Error('SAFETY_PROGRAM_RESPONSE_SCOPE_MISMATCH');
    return result;
  };
  return {
    async certificate(action,input) {
      current();const client=await port;current();if(!client.certificate)throw Error('CERTIFICATE_SERVICE_UNAVAILABLE');
      const response=await client.certificate(action,{...input,siteId});current();return response;
    },
    async upload(aggregateId, file, onProgress, signal) {
      current();
      if (file.size <= 0 || file.size > 20 * 1024 * 1024) throw Error('파일은 20MB 이내로 선택해 주세요.');
      const header=new TextDecoder().decode(await file.slice(0,5).arrayBuffer());
      const officeMime=file.name.toLowerCase().endsWith('.xlsx')?'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':file.name.toLowerCase().endsWith('.docx')?'application/vnd.openxmlformats-officedocument.wordprocessingml.document':null;
      const mimeType=header==='%PDF-'?'application/pdf':officeMime??await (await import('../media/album-photo')).albumPhotoType(file);
      if(!mimeType)throw Error('PDF, 사진, XLSX 또는 DOCX 파일을 선택해 주세요.');
      const bytes = await file.arrayBuffer();
      const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))).map(n=>n.toString(16).padStart(2,'0')).join('');
      current(); if(signal.aborted) throw Error('업로드가 취소되었습니다.');
      const client = await port;
      current();if(signal.aborted)throw Error('업로드가 취소되었습니다.');
      const prepared = await client.evidence('prepare',{siteId,aggregateId,fileName:file.name,mimeType,byteSize:file.size,contentHash:hash,origin:window.location.origin}) as {mediaManifestId:string;uploadUrl:string;headers:Record<string,string>};
      current();
      if(!prepared?.mediaManifestId || new URL(prepared.uploadUrl).protocol !== 'https:') throw Error('업로드 경로를 확인하지 못했습니다.');
      await new Promise<void>((resolve,reject)=>{
        const request = new XMLHttpRequest();
        const abort=()=>request.abort();
        const finish=(error?:Error)=>{signal.removeEventListener('abort',abort);if(error)reject(error);else resolve();};
        request.open('PUT',prepared.uploadUrl);request.timeout=90000;
        for(const [name,value] of Object.entries(prepared.headers)) request.setRequestHeader(name,value);
        request.upload.onprogress=event=>{if(event.lengthComputable)onProgress(Math.min(95,Math.round(event.loaded/event.total*95)));};
        request.onload=()=>finish(request.status>=200&&request.status<300?undefined:Error('사진·문서를 전송하지 못했습니다.'));
        request.onerror=()=>finish(Error('연결이 끊겼습니다. 다시 시도해 주세요.'));
        request.ontimeout=()=>finish(Error('전송 시간이 초과되었습니다. 다시 시도해 주세요.'));
        request.onabort=()=>finish(Error('업로드가 취소되었습니다.'));
        if(signal.aborted){reject(Error('업로드가 취소되었습니다.'));return;}
        signal.addEventListener('abort',abort,{once:true});request.send(file);
      });
      current();if(signal.aborted)throw Error('업로드가 취소되었습니다.');
      const committed=await client.evidence('commit',{siteId,mediaManifestId:prepared.mediaManifestId}) as SafetyProgramEvidence;
      current();
      if(committed?.mediaManifestId!==prepared.mediaManifestId || committed.contentHash!==hash)throw Error('원본 등록을 확인하지 못했습니다.');
      onProgress(100);return committed;
    },
    async open(evidence, disposition='attachment') {
      current();const client=await port;current();
      const result=await client.evidence('open',{siteId,mediaManifestId:evidence.mediaManifestId,disposition}) as {url:string};current();
      if(!result?.url || new URL(result.url).protocol!=='https:')throw Error('원본을 열지 못했습니다.');
      return result.url;
    },
    async load(year) {
      const result = await scoped(year, 'workspace');
      if (![result.plans, result.equipment, result.activityRecords, result.inspectionRecords, result.planResults, result.reminders].every(Array.isArray))
        throw Error('SAFETY_PROGRAM_RESPONSE_INVALID');
      return result;
    },
    async summary(year) {
      const result = await scoped(year, 'summary');
      if (!Array.isArray(result.monthlyPlans) || !Array.isArray(result.reminders) || !Array.isArray(result.inspectionSchedule) ||
        !result.inspectionSchedule.every(row=>row&&typeof row.equipmentId==='string'&&row.equipmentId&&typeof row.name==='string'&&row.name&&typeof row.dueDate==='string'&&/^\d{4}-\d{2}-\d{2}$/u.test(row.dueDate)&&Number.isFinite(Date.parse(`${row.dueDate}T00:00:00Z`))&&new Date(`${row.dueDate}T00:00:00Z`).toISOString().slice(0,10)===row.dueDate)) throw Error('SAFETY_PROGRAM_RESPONSE_INVALID');
      return result;
    },
    async save(operation, aggregateId, expectedVersion, payload, commandId) {
      current(); const client = await port; current();
      const response = await client.execute({ siteId, operation, aggregateId, expectedVersion, payload, commandId }); current();
      const result = response as {saved?: boolean; id?: string; version?: number} | null;
      if (!result?.saved || result.id !== aggregateId || result.version !== expectedVersion + 1) throw Error('SAFETY_PROGRAM_SAVE_UNCONFIRMED');
    },
  };
}
